import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, ChevronDown, ChevronUp, CheckCircle2, Clock, XCircle,
  FileText, User, Building2, Mail, Briefcase, Home, ChevronRight,
  Eye, EyeOff, Plus, Send, X, Copy, Check, RefreshCw, Star, Sparkles,
  AlertCircle, TrendingUp, BarChart3, MessageCircle
} from 'lucide-react';
import apiClient from '../services/authService';
import LeaseSetupModal from './LeaseSetupModal';

const STATUS_CONFIG = {
  invited:      { label: 'Invited',      color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  draft:        { label: 'Draft',        color: 'bg-gray-100 text-gray-600 border-gray-200' },
  in_progress:  { label: 'In Progress',  color: 'bg-blue-50 text-blue-700 border-blue-200' },
  submitted:    { label: 'Submitted',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  under_review: { label: 'Under Review', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  approved:     { label: 'Approved',     color: 'bg-green-50 text-green-700 border-green-200' },
  rejected:     { label: 'Rejected',     color: 'bg-red-50 text-red-700 border-red-200' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function StatusPicker({ currentStatus, appId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function pick(e, status) {
    e.stopPropagation();
    if (status === currentStatus) { setOpen(false); return; }
    setSaving(true);
    try {
      await apiClient.patch(`/api/applications/${appId}/status`, { status });
      onChanged(status);
    } catch (err) {
      console.error('Status update failed', err);
    } finally {
      setSaving(false);
      setOpen(false);
    }
  }

  const cfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.draft;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={saving}
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border transition-opacity hover:opacity-80 ${cfg.color} ${saving ? 'opacity-50' : ''}`}
      >
        {cfg.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[150px]">
          {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'invited').map(([k, v]) => (
            <button
              key={k}
              onClick={e => pick(e, k)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${k === currentStatus ? 'font-semibold' : ''}`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${v.color.split(' ')[0]}`} />
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Score chip ──────────────────────────────────────────────────────────────

function ScoreChip({ score }) {
  if (!score) return null;
  const color =
    score >= 8 ? 'bg-green-50 text-green-700 border-green-200' :
    score >= 6 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border ${color}`}>
      <Star className="w-3 h-3" />
      {score}/10
    </span>
  );
}

// ─── Detail panel sub-components ─────────────────────────────────────────────

function SectionCard({ title, icon: Icon, complete, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="flex-1 text-sm font-medium text-gray-700">{title}</span>
        {complete !== undefined && (
          complete
            ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
            : <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-4 bg-white text-sm text-gray-700 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-4">
      <span className="text-xs text-gray-400 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800 break-all">{value}</span>
    </div>
  );
}

function MaskedField({ label, value, masked }) {
  const [show, setShow] = useState(false);
  if (!value) return null;
  return (
    <div className="flex gap-4 items-center">
      <span className="text-xs text-gray-400 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-mono">{show ? value : masked}</span>
      <button
        onClick={() => setShow(v => !v)}
        className="text-gray-400 hover:text-gray-600 ml-1"
        title={show ? 'Hide' : 'Show'}
      >
        {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

// ─── Niva Score card ─────────────────────────────────────────────────────────

function NivaScoreCard({ nivaScore, nivaAssessment }) {
  if (!nivaScore && !nivaAssessment) {
    return (
      <div className="border border-dashed border-gray-200 rounded-xl px-4 py-4 flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-gray-300 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-400">Niva Tenant Score</p>
          <p className="text-xs text-gray-400 mt-0.5">Score will appear here once the application is submitted.</p>
        </div>
      </div>
    );
  }

  const score = nivaScore;
  const scoreColor =
    score >= 8 ? { ring: 'ring-green-300', bg: 'bg-green-50', text: 'text-green-700', bar: 'bg-green-500' } :
    score >= 6 ? { ring: 'ring-amber-300', bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500' } :
                 { ring: 'ring-red-300',   bg: 'bg-red-50',   text: 'text-red-700',   bar: 'bg-red-500'   };

  return (
    <div className={`rounded-xl border ${scoreColor.ring} ring-1 overflow-hidden`}>
      {/* Header row */}
      <div className={`${scoreColor.bg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Sparkles className={`w-4 h-4 ${scoreColor.text}`} />
          <span className={`text-sm font-semibold ${scoreColor.text}`}>Niva Tenant Score</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-3xl font-bold ${scoreColor.text}`}>{score}</span>
          <span className={`text-sm ${scoreColor.text} opacity-60`}>/10</span>
        </div>
      </div>

      {/* Summary */}
      {nivaAssessment?.summary && (
        <div className="px-4 py-3 border-t border-gray-100 bg-white">
          <p className="text-sm text-gray-700 leading-relaxed">{nivaAssessment.summary}</p>
          {nivaAssessment.generatedAt && (
            <p className="text-[10px] text-gray-400 mt-1.5">
              Generated {new Date(nivaAssessment.generatedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Dimensions */}
      {nivaAssessment?.dimensions?.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-white space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            Dimension Breakdown
          </p>
          {nivaAssessment.dimensions.map((dim, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">{dim.name}</span>
                <span className="text-xs font-bold text-gray-600">{dim.score}/10</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                <div
                  className={`h-1.5 rounded-full ${dim.score >= 8 ? 'bg-green-500' : dim.score >= 6 ? 'bg-amber-400' : 'bg-red-400'}`}
                  style={{ width: `${(dim.score / 10) * 100}%` }}
                />
              </div>
              {dim.comment && <p className="text-xs text-gray-500">{dim.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Niva Thread (landlord read-only view) ────────────────────────────────────

function NivaThreadPanel({ appId }) {
  const [messages, setMessages] = useState(null); // null = loading
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!appId) return;
    apiClient.get(`/api/application-messages/${appId}`)
      .then(r => setMessages(r.data))
      .catch(() => setMessages([]));
  }, [appId]);

  const count = messages?.length || 0;

  function formatTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <MessageCircle className="w-4 h-4 text-[#1D9E75] flex-shrink-0" />
        <span className="flex-1 text-sm font-medium text-gray-700">Niva Coordination Thread</span>
        {messages === null ? (
          <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        ) : count > 0 ? (
          <span className="text-xs bg-[#1D9E75]/10 text-[#1D9E75] font-semibold px-2 py-0.5 rounded-full">
            {count} message{count !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className="text-xs text-gray-400">No messages yet</span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="bg-white px-4 py-3">
          {messages === null ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-cyan-500 rounded-full animate-spin" />
            </div>
          ) : count === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              Niva will message the applicant once the application is scored.
            </p>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {messages.map((msg, i) => {
                const isNiva = msg.senderType === 'Niva';
                return (
                  <div key={msg._id || i} className={`flex gap-2 ${isNiva ? '' : 'flex-row-reverse'}`}>
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${isNiva ? 'bg-[#1a1a2e]' : 'bg-gray-200'}`}>
                      {isNiva
                        ? <Sparkles className="w-3 h-3 text-[#1D9E75]" />
                        : <User className="w-3 h-3 text-gray-500" />}
                    </div>
                    <div className={`max-w-[82%]`}>
                      <div className={`px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                        isNiva
                          ? 'bg-[#f0fdf4] text-gray-700 rounded-tl-sm border border-green-100'
                          : 'bg-gray-100 text-gray-700 rounded-tr-sm'
                      }`}>
                        {msg.content}
                      </div>
                      <p className={`text-[10px] text-gray-400 mt-0.5 ${isNiva ? 'ml-1' : 'text-right mr-1'}`}>
                        {isNiva ? 'Niva' : 'Applicant'} · {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[10px] text-gray-400 mt-3 pt-3 border-t border-gray-100 text-center">
            Niva handles all applicant communication. You don't need to get involved
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function ApplicationDetail({ app, onClose, onReview, onStatusChanged }) {
  const [localApp, setLocalApp] = useState(app);
  const [rescoring, setRescoring] = useState(false);
  const [leaseModalOpen, setLeaseModalOpen] = useState(false);
  const completedSections = localApp.completedSections || [];
  const hasSection = s => completedSections.includes(s);

  async function handleRescore() {
    if (rescoring || !localApp._id) return;
    setRescoring(true);
    try {
      const { data } = await apiClient.post(`/api/applications/${localApp._id}/rescore`);
      setLocalApp(a => ({ ...a, nivaScore: data.nivaScore, nivaAssessment: data.nivaAssessment }));
    } catch {
      // Silently fail — user can retry
    } finally {
      setRescoring(false);
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white flex-shrink-0">
        <div>
          <h2 className="font-semibold text-gray-900">
            {localApp.tenant?.name || localApp.applicantUser?.name || 'Applicant'}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {localApp.property?.name || localApp.property?.address}
            {localApp.submittedAt && (
              <span className="ml-2">· Submitted {new Date(localApp.submittedAt).toLocaleDateString()}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {localApp._id && !localApp._isInviteOnly && (
            <>
              <button
                onClick={handleRescore}
                disabled={rescoring}
                title="Re-evaluate with Niva AI"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#1D9E75] border border-gray-200 hover:border-[#1D9E75] rounded-lg transition-colors disabled:opacity-40"
              >
                {rescoring
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Evaluating…</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Re-evaluate</>
                }
              </button>
              <StatusPicker
                currentStatus={localApp.status}
                appId={localApp._id}
                onChanged={status => {
                  setLocalApp(a => ({ ...a, status }));
                  if (onStatusChanged) onStatusChanged(localApp._id, status);
                }}
              />
            </>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">

        {/* Niva Score */}
        <NivaScoreCard
          nivaScore={localApp.nivaScore}
          nivaAssessment={localApp.nivaAssessment}
        />

        {/* Niva coordination thread */}
        {localApp._id && !localApp._isInviteOnly && (
          <NivaThreadPanel appId={localApp._id} />
        )}

        <SectionCard title="Personal Information" icon={User} complete={hasSection('pii')}>
          {localApp.pii ? (
            <>
              <Field label="First Name" value={localApp.pii.firstName} />
              <Field label="Last Name" value={localApp.pii.lastName} />
              <Field label="Date of Birth" value={localApp.pii.dob} />
              <MaskedField label="SSN" value={localApp.pii.ssn} masked="•••-••-••••" />
              <Field label="Phone" value={localApp.pii.phone} />
              <Field label="Current Address" value={localApp.pii.currentAddress} />
            </>
          ) : <p className="text-gray-400 text-xs">Not yet filled in.</p>}
        </SectionCard>

        <SectionCard title="Driver's License" icon={FileText} complete={hasSection('driversLicense')}>
          {localApp.driversLicense ? (
            <>
              <MaskedField label="License #" value={localApp.driversLicense.licenseNumber} masked="••••••••••" />
              <Field label="Issuing State" value={localApp.driversLicense.issuingState} />
              <Field label="Expiry" value={localApp.driversLicense.expiryDate} />
              {localApp.driversLicense.documentSignedUrl ? (
                <div className="flex gap-4">
                  <span className="text-xs text-gray-400 w-32 flex-shrink-0">Document</span>
                  <a href={localApp.driversLicense.documentSignedUrl} target="_blank" rel="noreferrer"
                    className="text-cyan-600 hover:underline text-xs">View document</a>
                </div>
              ) : (
                <Field label="Document" value="Not uploaded" />
              )}
            </>
          ) : <p className="text-gray-400 text-xs">Not yet filled in.</p>}
        </SectionCard>

        <SectionCard title="Income & Employment" icon={Briefcase} complete={hasSection('income')}>
          {localApp.income ? (
            <>
              <Field label="Employer" value={localApp.income.employer} />
              <Field label="Job Title" value={localApp.income.jobTitle} />
              <Field label="Annual Income" value={localApp.income.annualIncome ? `$${localApp.income.annualIncome.toLocaleString()}` : null} />
              <Field label="Employment Type" value={localApp.income.employmentType} />
              <Field label="Start Date" value={localApp.income.startDate} />
            </>
          ) : <p className="text-gray-400 text-xs">Not yet filled in.</p>}
          {localApp.incomeDocuments?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">Income Documents ({localApp.incomeDocuments.length})</p>
              {localApp.incomeDocuments.map((doc, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  {doc.signedUrl ? (
                    <a href={doc.signedUrl} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline">{doc.fileName}</a>
                  ) : <span>{doc.fileName}</span>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Rental History" icon={Home} complete={hasSection('rentalHistory')}>
          {localApp.rentalHistory?.length > 0 ? localApp.rentalHistory.map((r, i) => (
            <div key={i} className={i > 0 ? 'pt-3 border-t border-gray-100' : ''}>
              <p className="text-xs font-medium text-gray-500 mb-1">Address {i + 1}</p>
              <Field label="Address" value={r.address} />
              <Field label="Landlord" value={r.landlordName} />
              <Field label="Landlord Phone" value={r.landlordPhone} />
              <Field label="Monthly Rent" value={r.monthlyRent ? `$${r.monthlyRent}` : null} />
              <Field label="Move In" value={r.moveInDate} />
              <Field label="Move Out" value={r.moveOutDate} />
              <Field label="Reason for Leaving" value={r.reasonForLeaving} />
            </div>
          )) : <p className="text-gray-400 text-xs">Not yet filled in.</p>}
        </SectionCard>

        <SectionCard title="References" icon={User} complete={hasSection('references')}>
          {localApp.references?.length > 0 ? localApp.references.map((r, i) => (
            <div key={i} className={i > 0 ? 'pt-3 border-t border-gray-100' : ''}>
              <p className="text-xs font-medium text-gray-500 mb-1">Reference {i + 1}</p>
              <Field label="Name" value={r.name} />
              <Field label="Relationship" value={r.relationship} />
              <Field label="Phone" value={r.phone} />
              <Field label="Email" value={r.email} />
            </div>
          )) : <p className="text-gray-400 text-xs">Not yet filled in.</p>}
        </SectionCard>

        {localApp.otherDocuments?.length > 0 && (
          <SectionCard title="Other Documents" icon={FileText}>
            {localApp.otherDocuments.map((doc, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                {doc.signedUrl ? (
                  <a href={doc.signedUrl} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline">{doc.fileName}</a>
                ) : <span>{doc.fileName}</span>}
                {doc.description && <span className="text-gray-400">({doc.description})</span>}
              </div>
            ))}
          </SectionCard>
        )}

        {localApp.reviewNotes && (
          <div className="border border-gray-200 rounded-xl px-4 py-3 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-1">Review Notes</p>
            <p className="text-sm text-gray-700">{localApp.reviewNotes}</p>
          </div>
        )}
      </div>

      {/* Review actions — only for submitted / under_review applications */}
      {(localApp.status === 'submitted' || localApp.status === 'under_review') && (
        <div className="border-t border-gray-200 px-6 py-4 flex gap-3 bg-white flex-shrink-0">
          <button
            onClick={() => onReview(localApp._id, 'rejected')}
            className="flex-1 py-2.5 border border-red-200 text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 transition-colors"
          >
            Reject
          </button>
          <button
            onClick={() => onReview(localApp._id, 'approved')}
            className="flex-1 py-2.5 bg-[#1D9E75] text-white text-sm font-medium rounded-xl hover:bg-[#178a65] transition-colors"
          >
            Approve
          </button>
        </div>
      )}

      {/* Setup Lease — shown after approval */}
      {localApp.status === 'approved' && !localApp._isInviteOnly && (
        <div className="border-t border-gray-200 px-6 py-4 bg-green-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Application Approved</p>
              <p className="text-xs text-green-600 mt-0.5">Create a lease record to activate NivaPay for this tenant.</p>
            </div>
            <button
              onClick={() => setLeaseModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1D9E75] text-white text-sm font-medium rounded-xl hover:bg-[#178a65] transition-colors whitespace-nowrap"
            >
              <FileText className="w-3.5 h-3.5" />
              Setup Lease
            </button>
          </div>
        </div>
      )}

      <LeaseSetupModal
        open={leaseModalOpen}
        onOpenChange={setLeaseModalOpen}
        tenantId={localApp.tenant?._id}
        tenantName={localApp.tenant?.name || localApp.applicantUser?.name}
        propertyId={localApp.property?._id}
        propertyName={localApp.property?.name || localApp.property?.address}
        onSaved={() => setLeaseModalOpen(false)}
      />
    </div>
  );
}

// ─── Send Invite Modal ────────────────────────────────────────────────────────

function SendInviteModal({ onClose, onSent }) {
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({ propertyId: '', prospectName: '', prospectEmail: '', sendEmail: true });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.get('/api/properties').then(r => setProperties(r.data)).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await apiClient.post('/api/invites', form);
      setResult({
        inviteCode: res.data.invite.inviteCode,
        applyUrl: res.data.applyUrl,
        prospectEmail: form.prospectEmail,
        emailSent: res.data.emailSent,
        emailError: res.data.emailError,
      });
      if (onSent) onSent();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(result.applyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Send Application Invite</h2>
            <p className="text-xs text-gray-500 mt-0.5">Prospect receives an email with a unique link</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Property *</label>
              <select
                required value={form.propertyId}
                onChange={e => setForm(f => ({ ...f, propertyId: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              >
                <option value="">Select a property…</option>
                {properties.map(p => (
                  <option key={p._id} value={p._id}>{p.name || p.address}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prospect Name *</label>
              <input required type="text" placeholder="e.g. Jane Smith"
                value={form.prospectName} onChange={e => setForm(f => ({ ...f, prospectName: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prospect Email *</label>
              <input required type="email" placeholder="jane@example.com"
                value={form.prospectEmail} onChange={e => setForm(f => ({ ...f, prospectEmail: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.sendEmail}
                onChange={e => setForm(f => ({ ...f, sendEmail: e.target.checked }))}
                className="w-4 h-4 accent-cyan-600 rounded" />
              <span className="text-sm text-gray-700">Send invite email automatically</span>
            </label>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 text-sm font-medium border border-gray-200 rounded-xl py-2.5 text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-medium bg-[#1D9E75] hover:bg-[#189060] text-white rounded-xl py-2.5 transition-colors disabled:opacity-60">
                <Send className="w-4 h-4" />
                {submitting ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">Invite created!</p>
                <p className="text-xs text-green-600">
                  {!form.sendEmail ? 'No email sent. Share the link below'
                    : result.emailSent ? `Email sent to ${result.prospectEmail}`
                    : 'Email could not be sent. Share the link below'}
                </p>
              </div>
            </div>
            {result.emailError && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <strong>Email not sent:</strong> {result.emailError}
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Application link</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <p className="text-xs text-gray-700 flex-1 truncate">{result.applyUrl}</p>
                <button onClick={copyLink} className="flex-shrink-0 text-gray-500 hover:text-[#1D9E75] transition-colors">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button onClick={onClose}
              className="w-full text-sm font-medium bg-[#1a1a2e] text-white rounded-xl py-2.5 hover:bg-[#2a2a4e] transition-colors">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── List row ─────────────────────────────────────────────────────────────────

function AppRow({ app, selected, unreadCount, onOpen, resendingId, resendResult, onResend, onStatusChanged }) {
  const totalSections = 5;
  const completedCount = app.completedSections?.length || 0;
  const isSelected = selected?._id === app._id;

  return (
    <div
      role={app._isInviteOnly ? undefined : 'button'}
      tabIndex={app._isInviteOnly ? undefined : 0}
      onClick={() => !app._isInviteOnly && onOpen(app)}
      onKeyDown={e => { if (!app._isInviteOnly && (e.key === 'Enter' || e.key === ' ')) onOpen(app); }}
      className={`w-full text-left px-4 py-3.5 border-b border-gray-100 flex items-center gap-3 transition-colors ${
        isSelected ? 'bg-cyan-50' : 'hover:bg-gray-50'
      } ${app._isInviteOnly ? 'cursor-default' : 'cursor-pointer'}`}
    >
      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-gray-400" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-medium text-gray-900 text-sm truncate">
            {app.tenant?.name || app.applicantUser?.name || app.applicantUser?.email || 'Unknown'}
          </span>
          {app._isInviteOnly
            ? <StatusBadge status={app.status} />
            : <StatusPicker
                currentStatus={app.status}
                appId={app._id}
                onChanged={status => onStatusChanged(app._id, status)}
              />
          }
          {app.nivaScore && <ScoreChip score={app.nivaScore} />}
          {unreadCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#1D9E75] text-white">
              <MessageCircle className="w-2.5 h-2.5" />{unreadCount}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
          <Building2 className="w-3 h-3 flex-shrink-0" />
          {app.property?.name || app.property?.address || 'Unknown property'}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {app._isInviteOnly ? (
          <button
            onClick={e => onResend(e, app.invite)}
            disabled={resendingId === app.invite}
            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${
              resendResult[app.invite] === 'sent' ? 'bg-green-50 border-green-200 text-green-700'
              : resendResult[app.invite] === 'error' ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-cyan-300 hover:text-cyan-700'
            }`}
          >
            {resendingId === app.invite ? <RefreshCw className="w-3 h-3 animate-spin" />
              : resendResult[app.invite] === 'sent' ? <Check className="w-3 h-3" />
              : <Send className="w-3 h-3" />}
            {resendResult[app.invite] === 'sent' ? 'Sent!' : resendResult[app.invite] === 'error' ? 'Failed' : 'Resend'}
          </button>
        ) : (
          <>
            <div className="flex gap-0.5">
              {Array.from({ length: totalSections }).map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < completedCount ? 'bg-[#1D9E75]' : 'bg-gray-200'}`} />
              ))}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Applications({ user }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [resendResult, setResendResult] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.userType !== 'Landlord') navigate('/dashboard');
    else loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadApplications() {
    try {
      setLoading(true);
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const [appsRes, invitesRes] = await Promise.all([
        apiClient.get(`/api/applications${params}`),
        apiClient.get('/api/invites'),
      ]);

      const apps = appsRes.data;
      const invites = invitesRes.data;

      const appInviteIds = new Set(apps.map(a => a.invite?.toString()).filter(Boolean));
      const pendingInvites = invites
        .filter(inv => ['pending', 'opened'].includes(inv.status) && !appInviteIds.has(inv._id?.toString()))
        .map(inv => ({
          _id: inv._id,
          _isInviteOnly: true,
          status: 'invited',
          tenant: { name: inv.prospectName || inv.tenant?.name || '', email: inv.prospectEmail || inv.tenant?.email || '' },
          property: inv.property,
          invite: inv._id,
          inviteCode: inv.inviteCode,
          completedSections: [],
          createdAt: inv.createdAt,
        }));

      setApplications([...pendingInvites, ...apps]);
    } catch (err) {
      console.error('Failed to load applications', err);
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(app) {
    try {
      const res = await apiClient.get(`/api/applications/${app._id}`);
      setSelected(res.data);
    } catch {
      setSelected(app);
    }
  }

  // Track unread Niva message counts per application
  const [unreadCounts, setUnreadCounts] = useState({});
  useEffect(() => {
    const appIds = applications.filter(a => !a._isInviteOnly && a._id).map(a => a._id);
    if (!appIds.length) return;
    Promise.all(
      appIds.map(id =>
        apiClient.get(`/api/application-messages/${id}/unread`)
          .then(r => ({ id, count: r.data.count }))
          .catch(() => ({ id, count: 0 }))
      )
    ).then(results => {
      const map = {};
      results.forEach(({ id, count }) => { if (count > 0) map[id] = count; });
      setUnreadCounts(map);
    });
  }, [applications]);

  async function handleResend(e, inviteId) {
    e.stopPropagation();
    setResendingId(inviteId);
    try {
      const res = await apiClient.post(`/api/invites/${inviteId}/resend`);
      setResendResult(prev => ({ ...prev, [inviteId]: res.data.emailSent ? 'sent' : 'error' }));
      setTimeout(() => setResendResult(prev => { const n = { ...prev }; delete n[inviteId]; return n; }), 3000);
    } catch {
      setResendResult(prev => ({ ...prev, [inviteId]: 'error' }));
      setTimeout(() => setResendResult(prev => { const n = { ...prev }; delete n[inviteId]; return n; }), 3000);
    } finally {
      setResendingId(null);
    }
  }

  async function handleReview(id, status) {
    try {
      await apiClient.put(`/api/applications/${id}/review`, { status });
      setSelected(null);
      loadApplications();
    } catch (err) {
      console.error('Review failed', err);
    }
  }

  const filtered = filterStatus
    ? applications.filter(a => a.status === filterStatus)
    : applications;

  const totalSubmitted = applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length;

  return (
    <div className="flex -m-4 md:-m-6 overflow-hidden" style={{ height: 'calc(100vh - 3.5rem)' }}>

      {/* ── Left panel — list ─────────────────────────────────────────── */}
      <div className={`flex flex-col border-r border-gray-200 bg-white flex-shrink-0 transition-all ${selected ? 'w-80' : 'flex-1'}`}>

        {/* List header */}
        <div className="px-4 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-cyan-50 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-cyan-600" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Applications</h1>
                {totalSubmitted > 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {totalSubmitted} awaiting review
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-1.5 text-xs font-medium bg-[#1D9E75] hover:bg-[#189060] text-white px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Invite
            </button>
          </div>

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* List body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-4 text-gray-400">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No applications yet.</p>
              <p className="text-xs mt-1">Send an invite to get started.</p>
            </div>
          ) : (
            filtered.map(app => (
              <AppRow
                key={app._id}
                app={app}
                selected={selected}
                unreadCount={unreadCounts[app._id] || 0}
                onOpen={openDetail}
                resendingId={resendingId}
                resendResult={resendResult}
                onResend={handleResend}
                onStatusChanged={(id, status) =>
                  setApplications(prev => prev.map(a => a._id === id ? { ...a, status } : a))
                }
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right panel — detail ──────────────────────────────────────── */}
      {selected ? (
        <div className="flex-1 overflow-hidden flex flex-col bg-white">
          <ApplicationDetail
            key={selected._id}
            app={selected}
            onClose={() => setSelected(null)}
            onReview={handleReview}
            onStatusChanged={(id, status) => {
              setApplications(prev => prev.map(a => a._id === id ? { ...a, status } : a));
            }}
          />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-300 bg-gray-50">
          <div className="text-center">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">Select an application to view details</p>
          </div>
        </div>
      )}

      {showInviteModal && (
        <SendInviteModal
          onClose={() => setShowInviteModal(false)}
          onSent={loadApplications}
        />
      )}
    </div>
  );
}
